import type { ExecutionEnvironmentDescriptor } from "@cmmd-forge/contracts";
import { Navigate, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";

import { AppSidebarLayout } from "../components/AppSidebarLayout";
import { CommandPalette } from "../components/CommandPalette";
import { ProjectLeadCompanionHost } from "../components/ProjectLeadCompanion";
import { WebSocketConnectionSurface } from "../components/WebSocketConnectionSurface";
import { useFirstAccountSetupRedirect } from "../components/onboarding/useFirstAccountSetupRedirect";
import { ProjectContentSearchOverlay } from "../components/search/ProjectContentSearchDialog";
import { useCmmdForgeAccountPreferences } from "../cmmd/forgeAccountPreferences";
import { handleCmmdAuthExpired } from "../environments/cmmdApi/cmmdAuthRedirect";
import {
  resolveFirstAccountSetupState,
  shouldUseCmmdFirstAccountSetupState,
} from "../lib/firstAccountSetup";
import { useServerConfig, useServerSettings } from "../rpc/serverState";
import { shouldForceLoginOnCmmdPreferencesError } from "./authenticatedAppSurface.logic";
import { useWorkspaceSideEffects } from "./authenticatedAppSurface.workspaceSideEffects";

export function AuthenticatedAppSurface(props: {
  readonly pathname: string;
  readonly primaryEnvironmentDescriptor: ExecutionEnvironmentDescriptor;
}) {
  const serverConfig = useServerConfig();
  const serverSetupState = useServerSettings().firstAccountSetup;
  const useCmmdSetupState = shouldUseCmmdFirstAccountSetupState(props.primaryEnvironmentDescriptor);
  const cmmdAccountPreferences = useCmmdForgeAccountPreferences(useCmmdSetupState);
  const preferencesForceLogin =
    useCmmdSetupState &&
    cmmdAccountPreferences.isError &&
    shouldForceLoginOnCmmdPreferencesError(cmmdAccountPreferences.error);

  useEffect(() => {
    if (!preferencesForceLogin) return;
    void handleCmmdAuthExpired(401);
  }, [preferencesForceLogin]);

  useWorkspaceSideEffects(
    cmmdAccountPreferences.data?.lastActiveOrganizationId,
    cmmdAccountPreferences.isSuccess,
  );

  const setupState = resolveFirstAccountSetupState({
    useCmmdState: useCmmdSetupState,
    cmmdState: cmmdAccountPreferences.data?.firstAccountSetup ?? null,
    serverState: serverSetupState,
  });
  const shouldRedirectToSetup = useFirstAccountSetupRedirect({
    isAuthenticated: true,
    pathname: props.pathname,
    state: setupState,
    applyCloudEnvironmentGate: useCmmdSetupState,
  });

  if (preferencesForceLogin) {
    return null;
  }
  if (useCmmdSetupState && cmmdAccountPreferences.isError) {
    throw cmmdAccountPreferences.error;
  }
  if (setupState === null) {
    return null;
  }

  if (serverConfig !== null && shouldRedirectToSetup) {
    return <Navigate to="/setup" replace />;
  }

  if (props.pathname === "/setup") {
    return (
      <>
        <CommandPalette>
          <Outlet />
        </CommandPalette>
        <ProjectContentSearchOverlay />
      </>
    );
  }

  return (
    <WebSocketConnectionSurface>
      <CommandPalette>
        <AppSidebarLayout>
          <Outlet />
          <ProjectLeadCompanionHost />
        </AppSidebarLayout>
      </CommandPalette>
      <ProjectContentSearchOverlay />
    </WebSocketConnectionSurface>
  );
}
